/**
 * The technician's offline READ cache (FM Technician rebuild B18, decision D8;
 * screen C22 /tech/sync).
 *
 * REWRITTEN IN PLACE (RECONCILE.md). This file used to hold an uncalled
 * IndexedDB cache of work orders plus a second status-update queue. The queue
 * half duplicated services/pendingQueue and is gone on purpose: there is ONE
 * write queue (pendingQueue, per person, localStorage). This file is only what
 * the phone READS without a signal.
 *
 * WHAT IS KEPT. GET /tech/offline-pack — my own open jobs with checklists, the
 * Take-job summary, the permits I hold, the asset list and manuals metadata
 * for my sites — stored per USER ID, so a shared phone never shows the night
 * technician's jobs to the day one. Refreshed on sign-in, when the app comes
 * back to the foreground, and when the connection returns (TechMeEffects).
 * Every screen that reads it shows "data from HH:MM" (useOfflineData).
 *
 * MANUALS. A manual, drawing or SOP is saved on the phone when it is OPENED
 * (not prefetched over mobile data), 50 MB at most per person; past that the
 * one opened longest ago goes first.
 *
 * SIGN-OUT clears this person's pack and manuals (D8). The unsent queue is NOT
 * touched — it is kept per person in pendingQueue and waits for its owner.
 *
 * THE SERVICE WORKER DOES NOT CACHE /api (vite.config.ts says why). IndexedDB
 * through this module is the offline store.
 *
 * Storage goes through the small OfflineStore interface below: IndexedDB in the
 * app, an in-memory store in tests (setOfflineStore), so the eviction and
 * per-person rules are tested against the same code.
 */
import { apiGet, apiPost, getSignedInUserId } from '@/utils/api';
import { orgTimeZone } from '@/utils/timezone';
import { fmTime } from '@/utils/fmTime';
import type { RoundDetail } from '@/components/Tech/roundsApi';

// ---------------------------------------------------------------------------
// The pack's shape (backend/routers/tech_me.py offline_pack)
// ---------------------------------------------------------------------------

export interface PackJob {
  kind: 'wo' | 'ticket' | 'inspection';
  id: string;
  ref: string;
  title: string;
  description?: string | null;
  priority?: string | null;
  status: string;
  wo_type?: string;
  building_id: string | null;
  site_name: string | null;
  location_detail?: string | null;
  asset_id?: string | null;
  started_at?: string | null;
  held?: boolean;
  hold_reason?: string | null;
  /** The hold's words as the online list says them (guide walk). Absent from
   *  a pack saved before then: components/Tech/offlineJobs falls back to the
   *  reason's own label (utils/workOrderHold). */
  hold_label?: string | null;
  scheduled_date?: string | null;
  checklist: unknown[];
  created_at?: string | null;
}
export interface PackTake {
  kind: 'wo' | 'ticket'; id: string; ref: string; title: string;
  priority: string | null; site_name: string | null; created_at: string | null;
}
export interface PackPermit {
  id: string; number: string; type: string; state: string; location: string; work: string;
  site_name: string | null; valid_from: string | null; valid_to: string | null;
  work_order_id: string | null; i_am_receiver: boolean;
  /** FM Technician final review: what the status word is worked out from
   *  (components/Tech/permitPill). Absent from a pack saved before then. */
  type_label?: string; site_id?: string; receiver_signed_at?: string | null;
  needs_my_signature?: boolean; exception_state?: 'none' | 'asked' | 'approved' | 'refused';
}
export interface PackAsset {
  id: string; code: string; name: string; status: string;
  building_id: string | null; site_name: string | null; location: string;
}
export interface PackManual {
  id: string; asset_id: string; asset_code: string; file_name: string; kind: string;
  content_type: string | null; file_size: number | null; url: string | null; url_expires_at: string | null;
}
export interface OfflinePack {
  user_id: string;
  generated_at: string;
  /** `timezone` once the server sends it (review R2-1 asks the backend). */
  sites: { id: string; name: string; timezone?: string | null }[];
  jobs: PackJob[];
  take: PackTake[];
  permits: PackPermit[];
  assets: PackAsset[];
  manuals: PackManual[];
  /** R3 (B30): the rounds at my sites — params, units, round points (never
   *  their codes) and each unit's last readings. Absent from a pack saved
   *  before R3. */
  rounds?: RoundDetail[];
  truncated: Record<string, boolean>;
  caps: Record<string, number>;
}
export type OfflineKey = 'jobs' | 'take' | 'permits' | 'assets' | 'manuals' | 'sites' | 'rounds';

interface StoredPack { userId: string; savedAt: number; pack: OfflinePack }
export interface StoredManual {
  userId: string; id: string; fileName: string; contentType: string | null;
  size: number; lastOpened: number; blob: Blob;
}

/** 50 MB of manuals per person on the phone (D8). */
export const MANUALS_CAP_BYTES = 50 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

export interface OfflineStore {
  getPack(userId: string): Promise<StoredPack | null>;
  putPack(p: StoredPack): Promise<void>;
  deletePack(userId: string): Promise<void>;
  listManuals(userId: string): Promise<StoredManual[]>;
  putManual(m: StoredManual): Promise<void>;
  deleteManual(userId: string, id: string): Promise<void>;
}

const DB_NAME = 'spaciora-offline';
/** v1 was the dead work-order cache; v2 drops its stores. */
const DB_VERSION = 2;
const PACKS = 'packs';
const MANUALS = 'manuals';

function idbStore(): OfflineStore {
  let opening: Promise<IDBDatabase> | null = null;
  const open = (): Promise<IDBDatabase> => {
    if (opening) return opening;
    opening = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('This browser cannot save data for offline use.'));
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        for (const old of ['work_orders', 'pending_updates', 'api_cache']) {
          if (db.objectStoreNames.contains(old)) db.deleteObjectStore(old);
        }
        if (!db.objectStoreNames.contains(PACKS)) db.createObjectStore(PACKS, { keyPath: 'userId' });
        if (!db.objectStoreNames.contains(MANUALS)) {
          const s = db.createObjectStore(MANUALS, { keyPath: ['userId', 'id'] });
          s.createIndex('userId', 'userId', { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => { opening = null; reject(req.error); };
    });
    return opening;
  };
  const run = async <T>(store: string, mode: IDBTransactionMode,
    fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(store, mode);
      const req = fn(tx.objectStore(store));
      tx.oncomplete = () => resolve(req.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  };
  return {
    getPack: async (userId) => (await run<StoredPack | undefined>(PACKS, 'readonly', (s) => s.get(userId))) ?? null,
    putPack: async (p) => { await run(PACKS, 'readwrite', (s) => s.put(p)); },
    deletePack: async (userId) => { await run(PACKS, 'readwrite', (s) => s.delete(userId)); },
    listManuals: async (userId) =>
      (await run<StoredManual[]>(MANUALS, 'readonly', (s) => s.index('userId').getAll(userId))) ?? [],
    putManual: async (m) => { await run(MANUALS, 'readwrite', (s) => s.put(m)); },
    deleteManual: async (userId, id) => { await run(MANUALS, 'readwrite', (s) => s.delete([userId, id])); },
  };
}

/** An in-memory OfflineStore (tests, and a browser with no IndexedDB). */
export function memoryStore(): OfflineStore {
  const packs = new Map<string, StoredPack>();
  const manuals = new Map<string, StoredManual>();
  const k = (u: string, id: string) => `${u}\u0000${id}`;
  return {
    getPack: async (u) => packs.get(u) ?? null,
    putPack: async (p) => { packs.set(p.userId, p); },
    deletePack: async (u) => { packs.delete(u); },
    listManuals: async (u) => [...manuals.values()].filter((m) => m.userId === u),
    putManual: async (m) => { manuals.set(k(m.userId, m.id), m); },
    deleteManual: async (u, id) => { manuals.delete(k(u, id)); },
  };
}

let store: OfflineStore | null = null;
function db(): OfflineStore {
  if (!store) store = typeof indexedDB === 'undefined' ? memoryStore() : idbStore();
  return store;
}
/** Swap the storage (tests). */
export function setOfflineStore(s: OfflineStore | null): void {
  store = s;
  current = null;
  emit();
}

// ---------------------------------------------------------------------------
// The current person's pack, shared by every screen
// ---------------------------------------------------------------------------

let current: StoredPack | null = null;
const listeners = new Set<() => void>();
function emit(): void { listeners.forEach((l) => { try { l(); } catch { /* a listener's bug is its own */ } }); }

export function subscribeOffline(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The pack in memory for `userId`, if it is the one loaded. */
export function currentPack(userId: string | null = getSignedInUserId()): StoredPack | null {
  return current && userId && current.userId === userId ? current : null;
}

/** Load this person's saved pack from the phone (no network). */
export async function loadPack(userId: string | null = getSignedInUserId()): Promise<StoredPack | null> {
  if (!userId) return null;
  if (current?.userId === userId) return current;
  let saved: StoredPack | null = null;
  try { saved = await db().getPack(userId); } catch { saved = null; }
  // Only ever this person's: a pack stored under one id is never shown to
  // another, whatever the row says.
  if (saved && saved.userId === userId && saved.pack?.user_id === userId && getSignedInUserId() === userId) {
    current = saved;
    emit();
  }
  return currentPack(userId);
}

/** Fetch a fresh pack and save it for the signed-in person. */
export async function refreshPack(): Promise<StoredPack | null> {
  const userId = getSignedInUserId();
  if (!userId) return null;
  const pack = await apiGet<OfflinePack>('/tech/offline-pack');
  // Signed out (or someone else signed in) while it was on the way: drop it.
  if (!pack || pack.user_id !== userId || getSignedInUserId() !== userId) return currentPack();
  const stored: StoredPack = { userId, savedAt: Date.now(), pack };
  await db().putPack(stored);
  current = stored;
  emit();
  return stored;
}

/** Sign-out (D8): this person's pack and manuals go; the unsent queue stays. */
export async function clearOfflineFor(userId: string | null): Promise<void> {
  if (!userId) return;
  if (current?.userId === userId) current = null;
  emit();
  try {
    await db().deletePack(userId);
    for (const m of await db().listManuals(userId)) await db().deleteManual(userId, m.id);
  } catch { /* nothing saved, nothing to clear */ }
}

/** "data from 10:42 IST" for a pack — on the technician's SITE clock, named
 *  (review R2-1; components/Tech/techZone). No zone given: the organisation's,
 *  still named. */
export function dataFromLabel(generatedAt: string | null | undefined, zone?: string | null): string | null {
  if (!generatedAt) return null;
  const d = new Date(generatedAt);
  if (Number.isNaN(d.getTime())) return null;
  return `data from ${fmTime(d, zone || orgTimeZone())}`;
}

// ---------------------------------------------------------------------------
// Manuals: saved when opened, 50 MB, oldest-opened evicted
// ---------------------------------------------------------------------------

/** Drop the longest-unopened manuals until `incoming` more bytes fit. */
async function makeRoom(userId: string, incoming: number, keepId?: string): Promise<void> {
  const all = (await db().listManuals(userId)).sort((a, b) => a.lastOpened - b.lastOpened);
  let total = all.reduce((n, m) => n + m.size, 0);
  for (const m of all) {
    if (total + incoming <= MANUALS_CAP_BYTES) break;
    if (m.id === keepId) continue;
    await db().deleteManual(userId, m.id);
    total -= m.size;
  }
}

export async function savedManuals(userId: string | null = getSignedInUserId()): Promise<StoredManual[]> {
  if (!userId) return [];
  try { return await db().listManuals(userId); } catch { return []; }
}

/**
 * The file for a manual: from the phone when it is saved there (and marked as
 * opened now), else downloaded through its signed link and saved — unless it
 * alone is bigger than the cap. Throws with a sentence when offline and not
 * saved.
 */
export async function openManual(doc: PackManual, fetcher: typeof fetch = fetch): Promise<Blob> {
  const userId = getSignedInUserId();
  if (!userId) throw new Error('Sign in to open this document.');
  const saved = (await savedManuals(userId)).find((m) => m.id === doc.id);
  if (saved) {
    await db().putManual({ ...saved, lastOpened: Date.now() });
    emit();
    return saved.blob;
  }
  if (!doc.url) throw new Error('This document has no link yet. Open it again when you are online.');
  let res: Response;
  try {
    res = await fetcher(doc.url);
  } catch {
    throw new Error('Not saved on this phone yet. Open it once while you have a signal.');
  }
  if (!res.ok) throw new Error('The link to this document has expired. Refresh and try again.');
  const blob = await res.blob();
  if (blob.size <= MANUALS_CAP_BYTES) {
    await makeRoom(userId, blob.size, doc.id);
    await db().putManual({ userId, id: doc.id, fileName: doc.file_name, contentType: doc.content_type,
      size: blob.size, lastOpened: Date.now(), blob });
    emit();
  }
  return blob;
}

/** What is saved on this phone for the person (C22). */
export async function offlineSummary(userId: string | null = getSignedInUserId()): Promise<{
  packBytes: number; generatedAt: string | null; manualCount: number; manualBytes: number;
}> {
  const pack = (await loadPack(userId)) ?? null;
  const manuals = await savedManuals(userId);
  return {
    packBytes: pack ? new Blob([JSON.stringify(pack.pack)]).size : 0,
    generatedAt: pack?.pack.generated_at ?? null,
    manualCount: manuals.length,
    manualBytes: manuals.reduce((n, m) => n + m.size, 0),
  };
}

// ---------------------------------------------------------------------------
// Conflicts (B18): a refused replay is also kept on the server
// ---------------------------------------------------------------------------

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const JOB_ROUTES: { re: RegExp; kind: (m: RegExpMatchArray) => 'wo' | 'ticket' | 'inspection' }[] = [
  { re: new RegExp(`^/tech/jobs/(ticket|wo|inspection)/(${UUID})(?:/|$)`), kind: (m) => m[1] as 'wo' },
  { re: new RegExp(`^/maintenance/work-orders/(${UUID})(?:/|$)`), kind: () => 'wo' },
  { re: new RegExp(`^/helpdesk/tickets/(${UUID})(?:/|$)`), kind: () => 'ticket' },
  { re: new RegExp(`^/compliance/inspections/(${UUID})(?:/|$)`), kind: () => 'inspection' },
];

/** The job a queued write was about, from its endpoint; null when it is not a
 *  job (a check-in, a reading) — those stay in the blocked list only. */
export function jobOf(endpoint: string): { kind: 'wo' | 'ticket' | 'inspection'; objectId: string } | null {
  const path = endpoint.split('?')[0] ?? endpoint;
  for (const r of JOB_ROUTES) {
    const m = path.match(r.re);
    const objectId = m?.[m.length - 1];
    if (m && objectId) return { kind: r.kind(m), objectId };
  }
  return null;
}

export interface RefusedWrite {
  id: string; label: string; endpoint: string; method: string; body: Record<string, unknown>;
  capturedAt: number; idempotencyKey?: string;
}

/**
 * POST /tech/conflicts for a replay the server refused (409/422), so the FM
 * sees both and nothing is lost even if this phone is wiped. The item stays in
 * the blocked list as well. Sent with an idempotency key derived from the
 * item's own, so a second refusal of the same item is kept once. Returns
 * whether the server kept it.
 */
export async function reportConflict(item: RefusedWrite, reason: string, status: number): Promise<boolean> {
  const job = jobOf(item.endpoint);
  if (!job) return false;
  try {
    await apiPost('/tech/conflicts', {
      kind: job.kind,
      object_id: job.objectId,
      reason: reason.slice(0, 2000) || `Refused (${status})`,
      client_at: new Date(item.capturedAt).toISOString(),
      payload: { label: item.label, endpoint: item.endpoint, method: item.method, body: item.body, status },
    }, { headers: { 'Idempotency-Key': `conflict-${(item.idempotencyKey || item.id).slice(0, 100)}` } });
    return true;
  } catch {
    return false;
  }
}
